// AddPrayer.js
import React, { useState, useEffect, useRef } from "react";
import { db, auth } from "../firebase";
import { collection, addDoc, Timestamp } from "firebase/firestore";
import { PRAYER_CATEGORIES } from "../constants";

function AddPrayer({ onPrayerAdded }) {
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState([]);
  const containerRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [isAnnonymous, setIsAnnonymous] = useState(false);

  // Lock page scroll when the modal is open
  useEffect(() => {
    if (!open) return;

    const body = document.body;
    const html = document.documentElement;

    const prevBodyOverflow = body.style.overflow;
    const prevHtmlOverflow = html.style.overflow;

    body.style.overflow = 'hidden';
    html.style.overflow = 'hidden';

    return () => {
      body.style.overflow = prevBodyOverflow || '';
      html.style.overflow = prevHtmlOverflow || '';
    };
  }, [open]);

  // Close when clicking outside of the AddPrayer component
  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [open]);

  // Close on Escape key for accessibility
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  const handleCategoryChange = (cat) => {
    setCategory((prev) =>
      prev.includes(cat)
        ? prev.filter((c) => c !== cat)
        : [...prev, cat]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");
    setSuccess(false);

    const user = auth.currentUser;

    if (!user) {
      setError("You must be logged in to post a prayer.");
      setIsSubmitting(false);
      return;
    }

    try {
      await addDoc(collection(db, "prayers"), {
        userId: user.uid,
        name: user.displayName || user.email,
        description,
        category,
        isAnnonymous,
        createdAt: Timestamp.now(),
      });
      setDescription("");
      setCategory([]);
      setIsAnnonymous(false);
      setSuccess(true);
      setIsSubmitting(false);
      
      // Notify parent component that a prayer was added
      if (onPrayerAdded) {
        onPrayerAdded();
      }
    } catch (error) {
      console.error("Error adding prayer: ", error);
      setError("Failed to add prayer. Please try again.");
      setIsSubmitting(false);
    }
  };

  return (
    <div className="add-prayer-container" aria-live="polite" ref={containerRef}>
      <div className="add-prayer-header">
        <button
          type="button"
          className="button-primary prayer-toggle"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-controls="add-prayer-content"
        >
          {open ? 'Close' : 'Pray With Me'}
        </button>
      </div>

      <div id="add-prayer-content" className={`add-prayer-content ${open ? "open" : ""}`}>
        <form onSubmit={handleSubmit} className="add-prayer-form">
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message" style={{ color: 'green', backgroundColor: '#ecfdf5', borderColor: '#bbf7d0' }}>Prayer added successfully!</div>}

          <textarea
            className="form-input"
            placeholder="Prayer details..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
            disabled={isSubmitting}
            rows={3}
            style={{ resize: 'vertical' }}
          />

          <div className="category-checkboxes" style={{ marginBottom: '1rem' }}>
            <label style={{ fontWeight: 'bold', marginBottom: 4, display: 'block' }}>Categories:</label>
            {PRAYER_CATEGORIES.map((cat) => (
              <label key={cat} style={{ marginRight: 12, display: 'inline-block' }}>
                <input
                  type="checkbox"
                  value={cat}
                  checked={category.includes(cat)}
                  onChange={() => handleCategoryChange(cat)}
                  disabled={isSubmitting}
                />
                {cat}
              </label>
            ))}
          </div>
          <div className="anonymous-checkbox" style={{ marginBottom: '1rem' }}>
            <label style={{ fontWeight: 'bold', marginBottom: 4, display: 'block' }}>
              <input
                type="checkbox"
                checked={isAnnonymous}
                onChange={() => setIsAnnonymous((prev) => !prev)}
                disabled={isSubmitting}
                style={{ marginRight: 8 }}
              />
              Post as Anonymous
            </label>
          </div>
          <button className="button-primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem'}}>
                <div className="loader" style={{width: '20px', height: '20px', borderWidth: '3px'}} aria-hidden="true" />
                <span>Adding Prayer...</span>
              </div>
            ) : 'Add Prayer'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default AddPrayer;
